import { Add as AddIcon } from "@mui/icons-material";
import { Box, Button } from "@mui/material";
import { type DateTime } from "luxon";
import { useCallback, useState } from "react";

import { Dialog } from "../../atoms/Dialog/Dialog";
import {
  Date,
  FIELD_NAME_DATE,
  Form,
  type FormProps,
  ResetButton,
  SubmitButton,
  type TextFieldValue,
} from "../../atoms/Form";
import { Description, FIELD_NAME_DESCRIPTION, FIELD_NAME_NAME, Name } from "../../molecules/Form";

// Centralized strings for internationalization
const tAddNewMilestone = "Add New Milestone";
const tAddMilestone = "Add Milestone";
const tCancel = "Cancel";
const tCreate = "Create Milestone";
const tDueDate = "Due Date";

const requiredFields = [FIELD_NAME_NAME, FIELD_NAME_DATE];

interface FormValues {
  [FIELD_NAME_NAME]: TextFieldValue;
  [FIELD_NAME_DESCRIPTION]: TextFieldValue;
  [FIELD_NAME_DATE]: DateTime | null;
}

interface NewMilestone {
  name: string;
  description: string;
  dueDate: Date;
}

interface MilestoneFormDialogProps {
  onAddMilestone: (milestone: NewMilestone) => void;
}

export const MilestoneFormDialog = ({ onAddMilestone }: MilestoneFormDialogProps) => {
  const [open, setOpen] = useState(false);

  const closeDialog = useCallback(() => setOpen(false), []);
  const openDialog = useCallback(() => setOpen(true), []);

  const handleSubmit = useCallback<FormProps<FormValues>["onSubmit"]>(
    (formValues: FormValues, form) => {
      const name = formValues[FIELD_NAME_NAME];
      const dueDate = formValues[FIELD_NAME_DATE];

      if (name && dueDate) {
        const milestone: NewMilestone = {
          description: formValues[FIELD_NAME_DESCRIPTION] || "",
          dueDate: dueDate.toJSDate(),
          name,
        };

        onAddMilestone(milestone);
        form?.reset();
        closeDialog();
      }
    },
    [onAddMilestone, closeDialog],
  );

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<AddIcon />}
        onClick={openDialog}
        sx={{
          minWidth: "auto",
          textTransform: "none",
        }}
      >
        {tAddMilestone}
      </Button>

      <Form<FormValues> onSubmit={handleSubmit}>
        <Dialog
          actions={
            <Box display="grid" gap="2rem" gridAutoColumns="minmax(0, 1fr)" gridAutoFlow="row" padding="1rem">
              <Name required />
              <Description />
              <Date
                label={tDueDate}
                datePickerProps={{
                  disablePast: true,
                }}
                required
              />
              <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
                <ResetButton onClick={closeDialog}>{tCancel}</ResetButton>
                <SubmitButton onClick={closeDialog} requiredFields={requiredFields}>
                  {tCreate}
                </SubmitButton>
              </Box>
            </Box>
          }
          dialogType="custom"
          maxWidth="sm"
          onDialogClose={closeDialog}
          open={open}
          title={tAddNewMilestone}
        />
      </Form>
    </>
  );
};
